import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './ForgePage.css';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { generatePortraitTrio, selectPortrait, generateEggPortraitBrowser, saveEggPortrait } from '../services/db';
import PortraitPicker from '../components/PortraitPicker';
import BreedingReport from '../components/BreedingReport';
import '../components/PortraitPicker.css';
import { numberToGrade, getOverallColor } from '../utils/grades';
import { FUNCTIONS_URL, SUPABASE_ANON_KEY } from '../services/supabaseConfig';

interface ForgePageProps {
  onNavigate?: (page: string) => void;
}

interface PlayerInfo {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  overall: number;
  gender: string;
  avatar_url?: string;
  non_tradeable?: boolean;
}

interface EggInfo {
  id: string;
  mother_id: string;
  father_id: string;
  motherName: string;
  fatherName: string;
  hatch_season: number;
  status: string;
  child_gender: string;
  created_at: string;
  child_id?: string;
}

interface StudListing {
  id: string;
  playerId: string;
  fee: number;
  breedCount: number;
  maxBreeds: number;
  isPublic: boolean;
  expiresAt: string;
  player: {
    name: string;
    position: string;
    overall: number;
      avatarUrl?: string;
    leagueId?: string;
  } | null;
}

interface MyListing {
  id: string;
  player_id: string;
  fee: number;
  breed_count_this_season: number;
  max_breeds: number;
  is_public: boolean;
  is_active: boolean;
  created_at: string;
  expires_at: string;
  season: number;
  player: {
    name: string;
    position: string;
    overall: number;
      avatarUrl?: string;
  } | null;
}

type ForgeTab = 'breed' | 'marketplace' | 'my-studs';

const FORGE_COST = 1000;
const MIN_PUBLIC_FEE = 4000;
const MIN_PRIVATE_FEE = 8000;

const RARITY_COLORS: Record<string, string> = {
  common: '#9ca3af',
  uncommon: '#22c55e',
  rare: '#60a5fa',
  epic: '#a855f7',
};

const ForgePage: React.FC<ForgePageProps> = ({ onNavigate }) => {
  const location = useLocation();
  const preselectedFemaleId = (location.state as any)?.femalePlayerId
    || new URLSearchParams(location.search).get('femaleId')
    || null;
  const [activeTab, setActiveTab] = useState<ForgeTab>('marketplace');

  // Breed tab state
  const [females, setFemales] = useState<PlayerInfo[]>([]);
  const [males, setMales] = useState<PlayerInfo[]>([]);
  const [eggs, setEggs] = useState<EggInfo[]>([]);
  const [selectedFemale, setSelectedFemale] = useState<PlayerInfo | null>(null);
  const [selectedMale, setSelectedMale] = useState<PlayerInfo | null>(null);
  const [balance, setBalance] = useState(0);
  const [breeding, setBreeding] = useState(false);
  const [hatching, setHatching] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [leagueId, setLeagueId] = useState<string | null>(null);

  // Breeding report state
  const [report, setReport] = useState<any>(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Hatch naming flow
  const [hatchFirstName, setHatchFirstName] = useState('');
  const [hatchLastName, setHatchLastName] = useState('');
  const [hatchNameSet, setHatchNameSet] = useState(false);

  // Track which egg is in the naming step (before actual hatch)
  const [namingEggId, setNamingEggId] = useState<string | null>(null);

  // Start naming flow for an egg
  const startHatchFlow = (eggId: string) => {
    setNamingEggId(eggId);
    setHatchFirstName('');
    setHatchLastName('');
    setHatchNameSet(false);
  };

  // Cancel naming flow
  const cancelNaming = () => {
    setNamingEggId(null);
    setHatchFirstName('');
    setHatchLastName('');
  };

  // Name confirmed — proceed to actual hatch
  const confirmNameAndHatch = () => {
    if (!namingEggId) return;
    setHatchNameSet(true);
    handleHatch(namingEggId);
  };

  // Hatch portrait flow
  const [hatchChild, setHatchChild] = useState<any>(null);
  const [portraitChoices, setPortraitChoices] = useState<string[]>([]);
  const [portraitSlots, setPortraitSlots] = useState<number[]>([]);
  const [portraitLoading, setPortraitLoading] = useState(false);
  const [portraitError, setPortraitError] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [selecting, setSelecting] = useState(false);

  // Marketplace tab state
  const [studListings, setStudListings] = useState<StudListing[]>([]);
  const [marketplaceLoading, setMarketplaceLoading] = useState(false);
  const [forgeBreeding, setForgeBreeding] = useState(false);

  // Stud market filters
  const [studPosFilter, setStudPosFilter] = useState<'all' | 'hitter' | 'pitcher'>('all');
  const [studOvrFilter, setStudOvrFilter] = useState<'all' | 'S' | 'A' | 'B' | 'C'>('all');
  // Active stud being considered — shows female picker
  const [activeStud, setActiveStud] = useState<StudListing | null>(null);

  // My studs tab state
  const [myListings, setMyListings] = useState<MyListing[]>([]);
  const [myStudMales, setMyStudMales] = useState<PlayerInfo[]>([]);
  const [selectedStudPlayer, setSelectedStudPlayer] = useState<PlayerInfo | null>(null);
  const [listingFee, setListingFee] = useState(MIN_PUBLIC_FEE);
  const [isPublicListing, setIsPublicListing] = useState(true);
  const [listingSubmitting, setListingSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'marketplace') loadMarketplace();
    if (activeTab === 'my-studs') loadMyStuds();
  }, [activeTab]);

  const getSession = async () => {
    if (!supabase) return null;
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  };

  const loadData = async () => {
    if (!supabase) return;
    const session = await getSession();
    if (!session) return;

    const userId = session.user.id;

    // Get user's league — first try team ownership, then fall back to owned player's league
    const { data: teams } = await supabase
      .from('teams')
      .select('league_id')
      .eq('owner_id', userId)
      .limit(1);

    if (teams && teams.length > 0 && teams[0].league_id) {
      setLeagueId(teams[0].league_id);
    } else {
      // No team — get league from the user's own players
      const { data: ownedPlayers } = await supabase
        .from('players')
        .select('league_id')
        .eq('user_id', userId)
        .not('league_id', 'is', null)
        .limit(1);
      if (ownedPlayers && ownedPlayers.length > 0) {
        setLeagueId(ownedPlayers[0].league_id);
      }
    }
    // Get balance
    const { data: userData } = await supabase
      .from('users')
      .select('balls_balance')
      .eq('id', userId)
      .single();
    setBalance(userData?.balls_balance || 0);

    // Only retired females can breed
    const { data: myFemales } = await supabase
      .from('players')
      .select('id, first_name, last_name, position, overall, gender, avatar_url, retired')
      .eq('user_id', userId)
      .eq('gender', 'female')
      .eq('is_scrub', false)
      .eq('retired', true)
      .limit(50);
    const femaleList = (myFemales || []) as PlayerInfo[];
    setFemales(femaleList);
    // Auto-select if navigated here from a female player profile
    if (preselectedFemaleId) {
      const match = femaleList.find(f => f.id === preselectedFemaleId);
      if (match) setSelectedFemale(match);
    }

    // Load eggs
    try {
      const res = await fetch(`${FUNCTIONS_URL}/breed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'list-eggs' }),
      });
      const data = await res.json();
      if (data.eggs) setEggs(data.eggs);
    } catch (e) {
      console.warn('Failed to load eggs:', e);
    }
  };

  const loadMales = async () => {
    if (!supabase) return;
    const session = await getSession();
    if (!session) return;

    try {
      const res = await fetch(`${FUNCTIONS_URL}/breed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'list-males' }),
      });
      const data = await res.json();
      if (data.males) setMales(data.males);
    } catch (e) {
      setError('Failed to load males');
    }
  };

  // ─── Marketplace ───
  const loadMarketplace = async () => {
    setMarketplaceLoading(true);
    try {
      const res = await fetch(`${FUNCTIONS_URL}/forge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ action: 'browse-studs' }),
      });
      const data = await res.json();
      if (data.listings) setStudListings(data.listings);
    } catch (e) {
      console.warn('Failed to load marketplace:', e);
    } finally {
      setMarketplaceLoading(false);
    }
  };

  // ─── My Studs ───
  const loadMyStuds = async () => {
    const session = await getSession();
    if (!session) return;

    // Load my listings
    try {
      const res = await fetch(`${FUNCTIONS_URL}/forge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'my-listings' }),
      });
      const data = await res.json();
      if (data.listings) setMyListings(data.listings);
    } catch (e) {
      console.warn('Failed to load my listings:', e);
    }

    // Only retired, non-scrub males can be listed as studs
    const userId = session.user.id;
    const { data: myMales } = await supabase!
      .from('players')
      .select('id, first_name, last_name, position, overall, gender, avatar_url, non_tradeable, retired')
      .eq('user_id', userId)
      .eq('gender', 'male')
      .eq('is_scrub', false)
      .eq('retired', true)
      .limit(50);
    setMyStudMales((myMales || []) as PlayerInfo[]);
  };

  const handleListStud = async () => {
    if (!selectedStudPlayer) return;
    const session = await getSession();
    if (!session) { setError('Not authenticated'); return; }

    setListingSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${FUNCTIONS_URL}/forge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'list-stud',
          playerId: selectedStudPlayer.id,
          fee: listingFee,
          isPublic: isPublicListing,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to list stud');
      } else {
        setSuccess(`${selectedStudPlayer.first_name} listed as stud for ${listingFee.toLocaleString()} $BALLS`);
        setSelectedStudPlayer(null);
        loadMyStuds();
      }
    } catch (e: any) {
      setError(e.message || 'Failed to list stud');
    } finally {
      setListingSubmitting(false);
    }
  };

  const handleUnlistStud = async (listingId: string) => {
    const session = await getSession();
    if (!session) return;

    try {
      const res = await fetch(`${FUNCTIONS_URL}/forge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'unlist-stud', listingId }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to unlist');
      } else {
        setSuccess('Stud unlisted');
        loadMyStuds();
      }
    } catch (e: any) {
      setError(e.message || 'Failed to unlist');
    }
  };

  // Generate blended portrait via Hetzner proxy — Replicate blocks cloud IPs + no browser CORS
  const REVE_PROXY = 'https://reve.simforgebaseball.com';
  const generateEggPortrait = async (eggId: string, motherAvatarUrl: string, fatherAvatarUrl: string, gender: string) => {
    const SUPABASE_URL = 'https://yawtkdwhvccveuttvffu.supabase.co';
    try {
      const prompt = `${gender} baseball player`;
      // Create via proxy
      const res = await fetch(REVE_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create', prompt, parent1Url: motherAvatarUrl, parent2Url: fatherAvatarUrl }),
      });
      const pred = await res.json();
      if (pred.error) throw new Error('Reve error: ' + pred.error);
      if (pred.status === 'succeeded') {
        const imageUrl = Array.isArray(pred.output) ? pred.output[0] : pred.output;
        const session = await getSession();
        if (!session) return;
        await fetch(`${FUNCTIONS_URL}/breed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: 'save-portrait', eggId, imageUrl }),
        });
        return;
      }
      if (!pred.id) throw new Error('No prediction id: ' + JSON.stringify(pred).slice(0, 100));
      // Poll via proxy
      for (let i = 0; i < 40; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const poll = await fetch(REVE_PROXY, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'poll', predictionId: pred.id }),
        });
        const result = await poll.json();
        if (result.status === 'succeeded') {
          const imageUrl = Array.isArray(result.output) ? result.output[0] : result.output;
          // Upload to Supabase hatchling-portraits via edge function proxy
          const session = await getSession();
          if (!session) return;
          const uploadRes = await fetch(`${FUNCTIONS_URL}/breed`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
            body: JSON.stringify({ action: 'save-portrait', eggId, imageUrl }),
          });
          const uploadData = await uploadRes.json();
          if (uploadData.portraitPath) console.log('[forge] Portrait saved:', uploadData.portraitPath);
          return;
        }
        if (result.status === 'failed') throw new Error('Prediction failed: ' + result.error);
      }
    } catch (e: any) {
      console.warn('[forge] Portrait generation failed (non-blocking):', e.message);
    }
  };

  const handleForgeBreed = async (listing: StudListing) => {
    if (!selectedFemale) {
      setError('Select a female first');
      return;
    }
    const session = await getSession();
    if (!session) { setError('Not authenticated'); return; }

    setForgeBreeding(true);
    setError(null);
    try {
      const res = await fetch(`${FUNCTIONS_URL}/forge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'forge-breed',
          listingId: listing.id,
          motherId: selectedFemale.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Forge breed failed');
      } else {
        const genderEmoji = data.egg.childGender === 'female' ? '♀' : '♂';
        setSuccess(`Egg created via forge! ${genderEmoji} hatches season ${data.egg.hatchSeason} — Cost: ${data.egg.cost?.toLocaleString()} $BALLS`);
        setBalance(prev => prev - (data.egg.cost || 0));
        loadData();
        // Generate blended portrait client-side (browser can call reve/remix, server IPs cannot)
        if (data.egg?.id && selectedFemale?.avatar_url && listing.player?.avatarUrl) {
          generateEggPortrait(data.egg.id, selectedFemale.avatar_url, listing.player.avatarUrl, data.egg.childGender || 'male');
        }
      }
    } catch (e: any) {
      setError(e.message || 'Forge breed failed');
    } finally {
      setForgeBreeding(false);
    }
  };

  // ─── Breeding report ───
  const fetchBreedingReport = async () => {
    if (!selectedFemale || !selectedMale || !supabase) return;
    setReportLoading(true);
    setError(null);
    setReport(null);

    const session = await getSession();
    if (!session) { setError('Not authenticated'); setReportLoading(false); return; }

    try {
      const res = await fetch(`${FUNCTIONS_URL}/breed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'breeding-report',
          motherId: selectedFemale.id,
          fatherId: selectedMale.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to generate report');
      } else {
        setReport(data);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  const handleMaleSelect = (male: PlayerInfo) => {
    setSelectedMale(male);
    setTimeout(() => fetchBreedingReport(), 0);
  };

  const handleBreed = async () => {
    if (!selectedFemale || !selectedMale || !supabase) return;
    setBreeding(true);
    setError(null);
    setSuccess(null);

    const session = await getSession();
    if (!session) { setError('Not authenticated'); setBreeding(false); return; }

    try {
      const res = await fetch(`${FUNCTIONS_URL}/breed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'breed',
          motherId: selectedFemale.id,
          fatherId: selectedMale.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Breeding failed');
      } else {
        const genderEmoji = '🥚';
        setSuccess(`Egg created! 🥚 hatches season ${data.egg.hatchSeason} — generating portrait...`);
        setSelectedFemale(null);
        setSelectedMale(null);
        setReport(null);
        setBalance(prev => prev - FORGE_COST);
        loadData();

        // Kick off client-side portrait generation (reve/remix blocked on server IPs)
        const eggId = data.egg.id;
        const motherAvatar = data.egg.parentAvatars?.mother;
        const fatherAvatar = data.egg.parentAvatars?.father;
        if (motherAvatar && fatherAvatar) {
          generateEggPortraitBrowser(motherAvatar, fatherAvatar, [], data.egg.childGender || 'male')
            .then((imageUrl) => saveEggPortrait(eggId, imageUrl))
            .then(() => { setSuccess(`🥚 Egg hatches season ${data.egg.hatchSeason} — portrait ready!`); loadData(); })
            .catch((e) => console.warn('[ForgePage] Background portrait generation failed:', e.message));
        }
      }
    } catch (e: any) {
      setError(e.message || 'Breeding failed');
    } finally {
      setBreeding(false);
    }
  };

  const handleHatch = async (eggId: string) => {
    if (!supabase) return;
    setHatching(eggId);
    setError(null);

    const session = await getSession();
    if (!session) { setError('Not authenticated'); setHatching(null); return; }

    try {
      const res = await fetch(`${FUNCTIONS_URL}/breed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'hatch',
          eggId,
          firstName: hatchFirstName || undefined,
          lastName: hatchLastName || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Hatching failed');
      } else {
        setHatchChild(data.child);
        setNamingEggId(null);
        setPortraitLoading(true);
        setPortraitError(null);
        try {
          const portraitResult = await generatePortraitTrio(
            data.child.id,
            `${data.child.firstName} ${data.child.lastName}`,
            data.child.position,
            data.child.isHitter,
            data.child.gender
          );
          setPortraitChoices(portraitResult.choices);
          setPortraitSlots(portraitResult.slots);
          if (portraitResult.errors && portraitResult.errors.length > 0) {
            setPortraitError(portraitResult.errors.join('; '));
          }
        } catch (e: any) {
          setPortraitError(e.message || 'Portrait generation failed');
        } finally {
          setPortraitLoading(false);
        }
      }
    } catch (e: any) {
      setError(e.message || 'Hatching failed');
    } finally {
      setHatching(null);
    }
  };

  const handlePortraitSelect = async (index: number) => {
    if (!hatchChild) return;
    const slot = portraitSlots[index];
    if (!slot) return;

    if (selectedSlot !== index) {
      setSelectedSlot(index);
      return;
    }

    setSelecting(true);
    try {
      await selectPortrait(hatchChild.id, slot);
      setSuccess(`Your ${hatchChild.gender === 'female' ? 'daughter' : 'son'} is ready!`);
      setHatchChild(null);
      setPortraitChoices([]);
      setSelectedSlot(null);
      loadData();
    } catch (e: any) {
      setError(e.message || 'Failed to select portrait');
    } finally {
      setSelecting(false);
    }
  };

  // ─── Render ───

  // Hatch portrait flow
  if (hatchChild) {
    return (
      <div className="forge-page">
        <h2>🥚 Your Egg Has Hatched!</h2>
        <div className="hatch-result">
          <div className="hatch-info">
            <span className="gender-badge" style={{ background: hatchChild.gender === 'female' ? '#e91e8c' : '#2196f3' }}>
              {hatchChild.gender === 'female' ? '♀' : '♂'}
            </span>
            <span className="hatch-name" style={{ color: '#f0ede6', fontSize: 20, fontWeight: 700 }}>
              {hatchChild.firstName} {hatchChild.lastName}
            </span>
            <span className="hatch-ovr">OVR {numberToGrade(hatchChild.overall)}</span>
            <span className="hatch-pos">{hatchChild.position}</span>
          </div>
        </div>
        <PortraitPicker
          choices={portraitChoices}
          loading={portraitLoading}
          canReroll={false}
          rerolling={false}
          onSelect={handlePortraitSelect}
          onReroll={() => {}}
          onSkip={() => { setHatchChild(null); loadData(); }}
          selectedSlot={selectedSlot}
          selecting={selecting}
          error={portraitError}
        />
      </div>
    );
  }

  // Breeding report
  if (report) {
    return (
      <div className="forge-page">
        <BreedingReport
          report={report}
          onBreed={handleBreed}
          onBack={() => { setReport(null); setSelectedMale(null); }}
          breeding={breeding}
          balance={balance}
        />
      </div>
    );
  }

  return (
    <div className="forge-page">
      <div className="forge-header">
        <h1>🔨 The Forge</h1>
        <p>Breed monsters, list studs, earn $BALLS</p>
        <div className="forge-balance">💰 {balance.toLocaleString()} $BALLS</div>
      </div>

      {error && <div className="forge-error" onClick={() => setError(null)}>{error}</div>}
      {success && <div className="forge-success" onClick={() => setSuccess(null)}>{success}</div>}

      {/* Tabs */}
      <div className="forge-tabs">
        <button className={`forge-tab ${activeTab === 'breed' ? 'active' : ''}`} onClick={() => setActiveTab('breed')}>
          🧬 Breed
        </button>
        <button className={`forge-tab ${activeTab === 'marketplace' ? 'active' : ''}`} onClick={() => setActiveTab('marketplace')}>
          🐴 Stud Market
        </button>
        <button className={`forge-tab ${activeTab === 'my-studs' ? 'active' : ''}`} onClick={() => setActiveTab('my-studs')}>
          📋 My Studs
        </button>

      </div>

      {/* ─── BREED TAB ─── */}
      {activeTab === 'breed' && (
        <>
          {/* Step 1: Pick Your Female */}
          <div className="forge-section">
            <h2>♀ Your Females</h2>
            <p className="section-desc">Select a female to breed</p>
            {females.length === 0 ? (
              <div className="empty-state">You don't own any female monsters. Create one first!</div>
            ) : (
              <div className="player-select-grid">
                {females.map(p => (
                  <div
                    key={p.id}
                    className={`selectable-player ${selectedFemale?.id === p.id ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedFemale(p);
                      setSelectedMale(null);
                      setReport(null);
                      loadMales();
                    }}
                  >
                    <span className="player-name">{p.first_name} {p.last_name}</span>
                    <span className="player-position">{p.position} · {numberToGrade(p.overall)} OVR</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Step 2: Pick a Male */}
          {selectedFemale && (
            <div className="forge-section">
              <h2>♂ Choose a Male</h2>
              <p className="section-desc">Select a male — then hit Forge to create an egg</p>
              {reportLoading && <div className="forge-loading">⏳ Generating breeding report...</div>}
              {males.length === 0 ? (
                <div className="empty-state">No males found in your collection. List your male on the Stud Market to breed.</div>
              ) : (
                <div className="player-select-grid">
                  {males.map(p => (
                    <div
                      key={p.id}
                      className={`selectable-player ${selectedMale?.id === p.id ? 'selected' : ''}`}
                      onClick={() => handleMaleSelect(p)}
                    >
                      {p.avatar_url && <img src={p.avatar_url} alt={p.first_name} style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', marginBottom: 4 }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                      <span className="player-name">{p.first_name} {p.last_name}</span>
                      <span className="player-position">{p.position} · {numberToGrade(p.overall)} OVR</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Inline forge CTA — visible once both parents selected */}
              {selectedMale && !reportLoading && (
                <div className="forge-ready-panel">
                  <div className="forge-pairing">
                    {selectedFemale.avatar_url && <img src={selectedFemale.avatar_url} alt="Mother" className="forge-parent-img" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                    <div className="forge-pairing-info">
                      <span className="forge-mother">♀ {selectedFemale.first_name} {selectedFemale.last_name} · {numberToGrade(selectedFemale.overall)} OVR</span>
                      <span className="forge-x">✕</span>
                      <span className="forge-father">♂ {selectedMale.first_name} {selectedMale.last_name} · {numberToGrade(selectedMale.overall)} OVR</span>
                    </div>
                    {selectedMale.avatar_url && <img src={selectedMale.avatar_url} alt="Father" className="forge-parent-img" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                  </div>
                  <button
                    className="forge-fire-btn"
                    onClick={handleBreed}
                    disabled={breeding}
                  >
                    {breeding ? '🔥 Forging...' : '🥚 Forge — Create Egg'}
                  </button>
                  <p className="forge-hint">Egg hatches at the start of next season's playoffs</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ─── MARKETPLACE TAB ─── */}
      {activeTab === 'marketplace' && (
        <div className="forge-section">
          <h2>🐴 Stud Market</h2>
          <p className="section-desc">{selectedFemale ? `Forging with ${selectedFemale.first_name} ${selectedFemale.last_name} — pick a stud below` : 'Pick a stud — then select your female to forge'}</p>

          {/* Pre-selected female banner */}
          {selectedFemale && (
            <div className="selected-female-banner">
              {selectedFemale.avatar_url
                ? <img src={selectedFemale.avatar_url} alt="" className="sfb-portrait" />
                : <div className="sfb-initials">{selectedFemale.first_name?.[0]}{selectedFemale.last_name?.[0]}</div>
              }
              <div className="sfb-info">
                <span className="sfb-name">{selectedFemale.first_name} {selectedFemale.last_name}</span>
                <span className="sfb-meta">{selectedFemale.position} · {numberToGrade(selectedFemale.overall)} OVR · Ready to forge</span>
              </div>
              <button className="sfb-clear" onClick={() => setSelectedFemale(null)}>Change</button>
            </div>
          )}

          {/* Filters */}
          <div className="stud-filters">
            <div className="stud-filter-group">
              {(['all', 'hitter', 'pitcher'] as const).map(f => (
                <button key={f} className={`filter-pill ${studPosFilter === f ? 'active' : ''}`} onClick={() => setStudPosFilter(f)}>
                  {f === 'all' ? 'All' : f === 'hitter' ? '⚾ Hitters' : '🥎 Pitchers'}
                </button>
              ))}
            </div>
            <div className="stud-filter-group">
              {(['all', 'S', 'A', 'B', 'C'] as const).map(f => (
                <button key={f} className={`filter-pill ${studOvrFilter === f ? 'active' : ''}`} onClick={() => setStudOvrFilter(f)}>
                  {f === 'all' ? 'All OVR' : f}
                </button>
              ))}
            </div>
          </div>

          {marketplaceLoading ? (
            <div className="empty-state">Loading studs...</div>
          ) : studListings.length === 0 ? (
            <div className="empty-state">No studs listed yet.</div>
          ) : (() => {
            const gradeToLetter = (ovr: number) => ovr >= 90 ? 'S' : ovr >= 80 ? 'A' : ovr >= 70 ? 'B' : 'C';
            const filtered = studListings.filter(l => {
              if (studPosFilter !== 'all') {
                const isPitcher = l.player?.position === 'P';
                if (studPosFilter === 'pitcher' && !isPitcher) return false;
                if (studPosFilter === 'hitter' && isPitcher) return false;
              }
              if (studOvrFilter !== 'all' && gradeToLetter(l.player?.overall || 0) !== studOvrFilter) return false;
              return true;
            });
            return filtered.length === 0 ? (
              <div className="empty-state">No studs match your filters.</div>
            ) : (
              <div className="stud-market-grid">
                {filtered.map(l => (
                  <div key={l.id} className={`stud-card ${activeStud?.id === l.id ? 'active' : ''} ${l.breedCount >= l.maxBreeds ? 'maxed' : ''}`}
                    onClick={() => { if (l.breedCount < l.maxBreeds) setActiveStud(activeStud?.id === l.id ? null : l); }}
                  >
                    <div className="stud-card-portrait">
                      {l.player?.avatarUrl
                        ? <img src={l.player.avatarUrl} alt="" />
                        : <div className="stud-initials">{l.player?.name?.[0] || '?'}</div>
                      }
                      {l.breedCount >= l.maxBreeds && <div className="stud-maxed-overlay">MAXED</div>}
                    </div>
                    <div className="stud-card-body">
                      <div className="stud-name">{l.player?.name || 'Unknown'}</div>
                      <div className="stud-meta">
                        <span className="stud-pos">{l.player?.position}</span>
                        <span className="stud-ovr">{numberToGrade(l.player?.overall || 0)}</span>
                      </div>
                      <div className="stud-fee">{l.fee.toLocaleString()} 💰</div>
                      <div className="stud-breeds">{l.breedCount}/{l.maxBreeds} breeds</div>
                    </div>

                    {/* Female picker — expands when stud is selected */}
                    {activeStud?.id === l.id && (
                      <div className="female-picker" onClick={e => e.stopPropagation()}>
                        <div className="female-picker-label">{selectedFemale ? 'Forging with:' : 'Select your female:'}</div>
                        {females.length === 0 ? (
                          <div className="female-picker-empty">You have no retired females.</div>
                        ) : (
                          <div className="female-picker-list">
                            {females.map(f => (
                              <div key={f.id}
                                className={`female-picker-row ${selectedFemale?.id === f.id ? 'selected' : ''}`}
                                onClick={() => setSelectedFemale(f)}
                              >
                                {f.avatar_url
                                  ? <img src={f.avatar_url} alt="" className="female-picker-portrait" />
                                  : <div className="female-picker-initials">{f.first_name?.[0]}{f.last_name?.[0]}</div>
                                }
                                <div className="female-picker-info">
                                  <span className="female-picker-name">{f.first_name} {f.last_name}</span>
                                  <span className="female-picker-meta">{f.position} · {numberToGrade(f.overall)} OVR</span>
                                </div>
                                {selectedFemale?.id === f.id && <span className="female-picker-check">✓</span>}
                              </div>
                            ))}
                          </div>
                        )}
                        {selectedFemale && (
                          <button
                            className="forge-confirm-btn"
                            disabled={forgeBreeding}
                            onClick={() => handleForgeBreed(l)}
                          >
                            {forgeBreeding ? 'Forging...' : `🧬 Forge — ${l.fee.toLocaleString()} 💰`}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* ─── MY STUDS TAB ─── */}
      {activeTab === 'my-studs' && (
        <div className="forge-section">
          <h2>📋 My Stud Listings</h2>

          {/* Active listings */}
          {myListings.length > 0 && (
            <div className="my-listings-list" style={{ marginBottom: 32 }}>
              {myListings.map(l => (
                <div key={l.id} className="my-listing-card">
                  <div className="listing-info">
                    <span className="player-name">{l.player?.name || 'Unknown'}</span>
                    <span className="breeds-used">
                      {l.breed_count_this_season}/{l.max_breeds} breeds · {l.is_public ? 'Public' : 'Private'}
                    </span>
                  </div>
                  <div className="listing-price">{l.fee.toLocaleString()} $BALLS</div>
                  <button className="cancel-btn" onClick={() => handleUnlistStud(l.id)}>
                    Unlist
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* List new stud */}
          <div className="listing-form">
            <h3>List a New Stud</h3>
            <p className="form-desc">List your tradeable male as a stud. Other players pay your fee to breed with him.</p>

            <div className="listing-rules">
              <h4>Rules</h4>
              <ul>
                <li>Only tradeable (bought/forged) males can be listed</li>
                <li>Public minimum fee: {MIN_PUBLIC_FEE.toLocaleString()} $BALLS</li>
                <li>Private minimum fee: {MIN_PRIVATE_FEE.toLocaleString()} $BALLS</li>
                <li>Max 20 breeds per season per stud</li>
                <li>You earn 50% of each forge fee (50% goes to treasury)</li>
                <li>Can't sell player on marketplace while listed</li>
              </ul>
            </div>

            {/* Player select */}
            {myStudMales.length === 0 ? (
              <div className="empty-state">No tradeable males available. Buy or forge a male first!</div>
            ) : (
              <div className="player-select-grid">
                {myStudMales.map(p => (
                  <div
                    key={p.id}
                    className={`selectable-player ${selectedStudPlayer?.id === p.id ? 'selected' : ''}`}
                    onClick={() => setSelectedStudPlayer(p)}
                  >
                    <span className="player-name">{p.first_name} {p.last_name}</span>
                    <span className="player-position">{p.position} · {numberToGrade(p.overall)} OVR</span>
                  </div>
                ))}
              </div>
            )}

            {selectedStudPlayer && (
              <>
                {/* Visibility toggle */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: '#9ca3af', fontSize: 14, marginRight: 16 }}>
                    <input
                      type="radio"
                      name="visibility"
                      checked={isPublicListing}
                      onChange={() => { setIsPublicListing(true); setListingFee(Math.max(listingFee, MIN_PUBLIC_FEE)); }}
                      style={{ marginRight: 6 }}
                    />
                    Public (min {MIN_PUBLIC_FEE.toLocaleString()})
                  </label>
                  <label style={{ color: '#9ca3af', fontSize: 14 }}>
                    <input
                      type="radio"
                      name="visibility"
                      checked={!isPublicListing}
                      onChange={() => { setIsPublicListing(false); setListingFee(Math.max(listingFee, MIN_PRIVATE_FEE)); }}
                      style={{ marginRight: 6 }}
                    />
                    Private (min {MIN_PRIVATE_FEE.toLocaleString()})
                  </label>
                </div>

                {/* Fee slider */}
                <div className="price-input">
                  <label style={{ color: '#9ca3af', fontSize: 14, display: 'block', marginBottom: 8 }}>
                    Stud Fee ($BALLS)
                  </label>
                  <input
                    type="range"
                    min={isPublicListing ? MIN_PUBLIC_FEE : MIN_PRIVATE_FEE}
                    max={50000}
                    step={500}
                    value={listingFee}
                    onChange={e => setListingFee(parseInt(e.target.value))}
                  />
                  <span className="price-display">{listingFee.toLocaleString()} $BALLS</span>
                </div>

                {/* Fee breakdown */}
                <div className="fee-breakdown">
                  <h4>Fee Breakdown (per forge)</h4>
                  <div className="fee-row">
                    <span>Buyer pays</span>
                    <span>{listingFee.toLocaleString()}</span>
                  </div>
                  <div className="fee-row">
                    <span>You earn (50%)</span>
                    <span className="positive">{Math.floor(listingFee * 0.5).toLocaleString()}</span>
                  </div>
                  <div className="fee-row">
                    <span>Treasury (50%)</span>
                    <span>{Math.floor(listingFee * 0.5).toLocaleString()}</span>
                  </div>
                </div>

                <button
                  className="list-btn"
                  disabled={listingSubmitting}
                  onClick={handleListStud}
                >
                  {listingSubmitting ? 'Listing...' : `List ${selectedStudPlayer.first_name} as Stud`}
                </button>
              </>
            )}
          </div>
        </div>
      )}


    </div>
  );
};

export default ForgePage;
